/*
 * Word-level diff for the agent's Review card. Pure (no DOM), so it can be
 * unit tested. Words are compared with an LCS; whitespace and line breaks are
 * carried along with the new text so its layout is kept.
 */

/** Past this many words per side, show whole-text before/after instead. */
export const MAX_DIFF_WORDS = 400;

function tokenize(text) {
    return String(text ?? "").match(/\s+|[^\s]+/g) ?? [];
}

const isSpace = (token) => /^\s+$/.test(token);

/**
 * @param {string} before
 * @param {string} after
 * @returns {{ segments: Array<{ type: "same"|"added"|"removed", text: string }>, changed: boolean, breaksOnly: boolean, fallback: boolean }}
 */
export function wordDiff(before, after) {
    const a = String(before ?? "");
    const b = String(after ?? "");
    if (a === b) return { segments: b ? [{ type: "same", text: b }] : [], changed: false, breaksOnly: false, fallback: false };

    const aWords = tokenize(a).filter((token) => !isSpace(token));
    const bTokens = tokenize(b);
    const bWords = bTokens.filter((token) => !isSpace(token));
    if (aWords.join(" ") === bWords.join(" ")) {
        return { segments: [{ type: "same", text: b }], changed: true, breaksOnly: true, fallback: false };
    }
    if (aWords.length > MAX_DIFF_WORDS || bWords.length > MAX_DIFF_WORDS) {
        return {
            segments: [{ type: "removed", text: a }, { type: "added", text: b }],
            changed: true,
            breaksOnly: false,
            fallback: true,
        };
    }

    // LCS table over words.
    const n = aWords.length;
    const m = bWords.length;
    const lcs = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
    for (let i = n - 1; i >= 0; i--) {
        for (let j = m - 1; j >= 0; j--) {
            lcs[i][j] = aWords[i] === bWords[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
        }
    }

    // Walk the new text token by token so its whitespace and line breaks stay.
    const segments = [];
    const push = (type, text) => {
        const last = segments[segments.length - 1];
        if (last && last.type === type) last.text += text;
        else segments.push({ type, text });
    };
    let i = 0;
    let j = 0;
    for (const token of bTokens) {
        if (isSpace(token)) {
            push(segments.length && segments[segments.length - 1].type === "added" ? "added" : "same", token);
            continue;
        }
        while (i < n && (aWords[i] !== token || lcs[i][j] !== lcs[i + 1][j + 1] + 1) && lcs[i + 1][j] >= lcs[i][j + 1]) {
            push("removed", aWords[i] + " ");
            i++;
        }
        if (i < n && aWords[i] === token && lcs[i][j] === lcs[i + 1][j + 1] + 1) {
            push("same", token);
            i++;
        } else {
            push("added", token);
        }
        j++;
    }
    while (i < n) {
        push("removed", (segments.length ? " " : "") + aWords[i]);
        i++;
    }
    // Spaces glued to the end of an added run read better as plain text.
    for (const segment of segments) {
        if (segment.type === "added" && /\s+$/.test(segment.text)) {
            const trail = segment.text.match(/\s+$/)[0];
            segment.text = segment.text.slice(0, -trail.length);
            segment.trail = trail;
        }
    }
    const flat = [];
    const add = (type, text) => {
        if (!text) return;
        const last = flat[flat.length - 1];
        if (last && last.type === type) last.text += text;
        else flat.push({ type, text });
    };
    for (const segment of segments) {
        add(segment.type, segment.text);
        if (segment.trail) add("same", segment.trail);
    }
    return { segments: flat, changed: true, breaksOnly: false, fallback: false };
}
